import React from 'react';
import { Check, Mail, TrendingUp } from 'lucide-react';
import { buildPlanMailto } from '../../utils/mailto';

const PlanCard = ({ plan, popular, t, language }) => (
  <div
    className={`relative bg-white rounded-2xl p-7 md:p-8 shadow-lg flex flex-col h-full ${
      popular ? 'border-2 border-blue-600 ring-2 ring-blue-200' : 'border border-gray-200'
    }`}
    data-testid={`plan-card-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
  >
    {popular && (
      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full shadow-md">
        {t.pricing.popular}
      </div>
    )}

    <div className="mb-6">
      <h4 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h4>
      <div className="flex items-baseline gap-1 mb-4">
        <span className="text-sm text-gray-500">{t.pricing.from}</span>
        <span className="text-4xl font-extrabold text-blue-600">{plan.price}</span>
        <span className="text-gray-500">{t.pricing.perMonth}</span>
      </div>
      <p className="text-gray-600 leading-relaxed text-sm">{plan.tagline}</p>
    </div>

    <ul className="space-y-3 mb-6 flex-grow">
      {plan.features.map((f) => (
        <li key={f} className="flex items-start gap-3">
          <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <span className="text-gray-700 text-sm leading-relaxed">{f}</span>
        </li>
      ))}
    </ul>

    <div className="bg-blue-50 rounded-lg p-3 mb-6 flex items-start gap-2">
      <TrendingUp className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
      <div className="text-xs">
        <span className="text-gray-600">{t.pricing.recommendedLabel}</span>{' '}
        <span className="font-semibold text-gray-900">{plan.recommendedSpend}</span>
      </div>
    </div>

    <a
      href={buildPlanMailto(language, plan.name)}
      target="_blank"
      rel="noopener noreferrer"
      className={`w-full text-center px-6 py-3 rounded-lg font-semibold transition-colors inline-flex items-center justify-center gap-2 ${
        popular
          ? 'bg-blue-600 text-white hover:bg-blue-700'
          : 'bg-gray-900 text-white hover:bg-blue-600'
      }`}
      data-testid={`plan-cta-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Mail className="w-4 h-4" />
      {t.pricing.ctaPlan}
    </a>
  </div>
);

const AddonCard = ({ addon, t, language }) => (
  <div className="relative max-w-3xl mx-auto bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-6 md:p-8 border border-dashed border-blue-300 shadow-sm scroll-reveal-scale">
    <span className="absolute -top-3 left-6 bg-blue-100 text-blue-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
      {t.pricing.addonBadge}
    </span>
    <div className="md:flex md:items-start md:gap-8">
      <div className="flex-1 mb-6 md:mb-0">
        <h4 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">{addon.name}</h4>
        <div className="flex items-baseline gap-1 mb-3">
          <span className="text-sm text-gray-500">{t.pricing.from}</span>
          <span className="text-3xl font-bold text-blue-600">{addon.price}</span>
          <span className="text-gray-500">{t.pricing.perMonth}</span>
        </div>
        <p className="text-gray-600 text-sm mb-4">{addon.tagline}</p>
        <a
          href={buildPlanMailto(language, addon.name)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-white border-2 border-blue-600 text-blue-600 px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-blue-600 hover:text-white transition-colors"
          data-testid="addon-cta-instagram-light"
        >
          <Mail className="w-4 h-4" />
          {t.pricing.ctaAddon}
        </a>
      </div>
      <ul className="md:flex-1 space-y-2">
        {addon.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <Check className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <span className="text-gray-700">{f}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const PricingSection = ({ t, language }) => {
  const planKeys = ['launch', 'growth', 'scale'];

  return (
    <section id="precios" className="py-16 bg-white curtain-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14 scroll-reveal">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.pricing.title} <span className="text-gradient">{t.pricing.titleHighlight}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-3">{t.pricing.subtitle}</p>
          <p className="text-sm text-gray-500 italic">{t.pricing.adNote}</p>
        </div>

        <div className="text-center mb-10 scroll-reveal">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
            {t.pricing.metaCategory.title}
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {planKeys.map((key) => (
            <div key={key} className="scroll-reveal-scale">
              <PlanCard
                plan={t.pricing.metaCategory[key]}
                popular={key === 'growth'}
                t={t}
                language={language}
              />
            </div>
          ))}
        </div>

        <AddonCard addon={t.pricing.addon} t={t} language={language} />

        {/* Positioning */}
        <div className="bg-gradient-to-br from-gray-900 to-blue-900 rounded-2xl p-8 md:p-12 text-center scroll-reveal-scale shadow-xl mt-16">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
            {t.pricing.positioning.title}
          </h3>
          <p className="text-blue-100 mb-6 text-lg">{t.pricing.positioning.intro}</p>
          <ul className="max-w-2xl mx-auto space-y-3 text-left inline-block">
            {t.pricing.positioning.points.map((point) => (
              <li key={point} className="flex items-start gap-3 text-white">
                <Check className="w-5 h-5 text-blue-300 flex-shrink-0 mt-1" />
                <span className="text-base md:text-lg">{point}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
